import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { fromEvent, merge, Subscription, timer } from 'rxjs';

import { AuthService } from './auth.service';
import { environment } from '@env';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService implements OnDestroy {
  private readonly idleTimeoutMs = (environment as any).sessionTimeoutMinutes
    ? (environment as any).sessionTimeoutMinutes * 60 * 1000
    : 15 * 60 * 1000; // default 15 minutes

  private activitySubscription: Subscription | null = null;
  private timeoutSubscription: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Starts watching for user activity and triggers logout after inactivity.
   * Call once during app bootstrap (e.g. in AppComponent.ngOnInit).
   */
  startWatching(): void {
    if (this.activitySubscription) {
      return;
    }

    const activityEvents$ = merge(
      fromEvent(this.document, 'mousemove'),
      fromEvent(this.document, 'mousedown'),
      fromEvent(this.document, 'keydown'),
      fromEvent(this.document, 'scroll'),
      fromEvent(this.document, 'touchstart')
    );

    this.ngZone.runOutsideAngular(() => {
      this.activitySubscription = activityEvents$.subscribe(() => {
        this.resetTimer();
      });
    });

    // start initial timer
    this.resetTimer();
  }

  stopWatching(): void {
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
    this.clearTimer();
  }

  private resetTimer(): void {
    this.clearTimer();

    this.timeoutSubscription = timer(this.idleTimeoutMs).subscribe(() => {
      this.handleTimeout();
    });
  }

  private clearTimer(): void {
    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
      this.timeoutSubscription = null;
    }
  }

  private handleTimeout(): void {
    this.ngZone.run(() => {
      if (this.authService.isLoggedIn()) {
        this.authService.logout();
      }
      this.router.navigate(['/bl/auth/sign-in']);
    });
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }
}


